import { useEffect, useRef, type RefObject, type ReactNode } from "react";

import { Button } from "@go-novus/ui";

/** 提前一屏开始拉，滚到底时下一页通常已经在了。 */
const PREFETCH_MARGIN = "200px";

export interface InfiniteScrollProps {
  hasNextPage: boolean;
  isFetchingNextPage: boolean;
  /** 平时藏起来、聚焦才现身的翻页按钮文案：鼠标用户靠滚，键盘与读屏用户靠它。 */
  label: string;
  /** 加载提示，同时播报给读屏；提供 loadingContent 时仅供读屏使用。 */
  loadingLabel: string;
  loadingContent?: ReactNode;
  onLoadMore: () => void;
  /**
   * 列表所在的滚动容器；列表跟着页面滚时不传。
   * 列表在 ScrollArea 里却不传 viewportRef，rootMargin 会按视口算：容器只有几行高时
   * 哨兵一挂上就命中，一口气白拉好几页。
   */
  rootRef?: RefObject<HTMLElement | null>;
}

/**
 * 滚到底自动拉下一页。翻页本身仍归调用方的 infinite query，这里只负责触发。
 */
export function InfiniteScroll({
  hasNextPage,
  isFetchingNextPage,
  label,
  loadingLabel,
  loadingContent,
  onLoadMore,
  rootRef,
}: InfiniteScrollProps) {
  const sentinelRef = useRef<HTMLDivElement>(null);
  const loadMoreRef = useRef(onLoadMore);

  useEffect(() => {
    loadMoreRef.current = onLoadMore;
  }, [onLoadMore]);

  useEffect(() => {
    const sentinel = sentinelRef.current;

    // 拉取期间不观察：拉完这个 effect 重跑，哨兵还露着就再拉一页，直到内容填满容器。
    // 少了这个条件，一次滚动会并发打出好几个请求。
    if (sentinel === null || isFetchingNextPage) {
      return;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting)) {
          loadMoreRef.current();
        }
      },
      { root: rootRef?.current ?? null, rootMargin: PREFETCH_MARGIN },
    );

    observer.observe(sentinel);

    return () => {
      observer.disconnect();
    };
    // hasNextPage 必须在依赖里。调用方把这个组件常驻在列表旁边时，首屏它是 false，
    // 哨兵没渲染，effect 空跑一次；第一页回来才翻成 true，依赖不变就不会重跑，
    // 哨兵一辈子没被 observe 上，那些页面的滚动加载一次都不触发。
  }, [hasNextPage, isFetchingNextPage, rootRef]);

  if (!hasNextPage) {
    return null;
  }

  return (
    // 下面的 sr-only 是 absolute。少了这层 relative，它会越过列表外面的滚动容器、按整页定位，
    // 把页面撑高，窗口多出第二根滚动条。
    <div className="relative">
      {isFetchingNextPage && (
        <>
          <div
            className={
              loadingContent === undefined
                ? "border-t border-[var(--border-surface-default)] p-2 text-center text-micro text-[var(--content-fg-subtle)]"
                : "sr-only"
            }
            role="status"
          >
            {loadingLabel}
          </div>
          {loadingContent}
        </>
      )}
      <div ref={sentinelRef} />
      {/* Button 自己吃掉 className，藏它只能靠外层；focus-within 让键盘 Tab 到时现身。
          边框和内距也挂在 focus-within 上：不带前缀的 p-2 会盖掉 sr-only 的 1px，
          现身时又被 not-sr-only 的 padding: 0 盖回去，等于只在藏着时生效。 */}
      <div className="sr-only border-[var(--border-surface-default)] focus-within:not-sr-only focus-within:border-t focus-within:p-2">
        <Button
          disabled={isFetchingNextPage}
          fullWidth
          onClick={onLoadMore}
          size="sm"
          variant="quiet"
        >
          {label}
        </Button>
      </div>
    </div>
  );
}
